import React from 'react';
import PropTypes from 'prop-types';

import Tag from '@decisiv/ui-components/lib/components/Tag';

import { TagRow } from '../../shared/styles';
import EMPTY_VALUE from '../../shared/emptyValue';

/** Password login is an ordinary outcome, not a gap, so it gets its own colour. */
const PASSWORD_COLOR = 'licoriceMousse';
const PROVIDER_COLOR = 'information';

/**
 * The AUTHENTICATION column: how this user signs in (PD-4684).
 *
 * A tag rather than plain text, so it reads as a state of the account the way the
 * PERMISSIONS column's roles do. Deliberately not interactive, unlike the IDP cell
 * on the legacy user access page (SsoAccessCell), which assigns a provider from a
 * popover: that page is scoped to one dealer and so knows which providers are on
 * offer, whereas this list spans accounts and cannot. Changing the provider is the
 * Edit User page's job here.
 *
 * A domain-assigned provider is not distinguished from a manually assigned one.
 * Both are the same fact to read off a list — which identity provider this user
 * signs in with — and the difference only matters where it can be acted on, which
 * is the edit form, where it decides whether the field is editable at all.
 */
function AuthenticationCell({ authentication }) {
  if (!authentication || !authentication.label) {
    return <span>{EMPTY_VALUE}</span>;
  }

  const { label, provider } = authentication;

  return (
    <TagRow>
      <Tag
        color={provider ? PROVIDER_COLOR : PASSWORD_COLOR}
        palette="pastel"
        text={label}
      />
    </TagRow>
  );
}

/** As Management::UserAuthentications sends it. */
export const AUTHENTICATION = PropTypes.shape({
  /** 'domain', 'manual', or null for password login. */
  assignment: PropTypes.string,
  /** The stored provider code, or null for password login. */
  provider: PropTypes.string,
  /** What to render: a provider's display name, or "Password". */
  label: PropTypes.string,
});

AuthenticationCell.propTypes = {
  authentication: AUTHENTICATION,
};

AuthenticationCell.defaultProps = {
  authentication: null,
};

export default AuthenticationCell;
